import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAbout } from '../../lib/use_about';
import { usePersonalizationOptional } from '../../lib/use_personalization';
import { useChildEditorReadOnly } from '../../lib/child_editors';

const MAX_WIDTH_CLASS: Record<string, string> = {
  sm:   'max-w-sm',
  md:   'max-w-md',
  lg:   'max-w-lg',
  xl:   'max-w-xl',
  '2xl':'max-w-2xl',
  '3xl':'max-w-3xl',
  '4xl':'max-w-4xl',
  // Near-viewport, for content that is read rather than filled in — a rendered
  // document has no useful "form width" to sit at.
  full: 'max-w-[96vw]',
};

interface ModalProps {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  /** Pinned content rendered between the tab bar and scrollable body — never scrolls away. */
  notice?: React.ReactNode;
  width?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | 'full';
  tabs?: { id: string; label: string; disabled?: boolean }[];
  activeTab?: string;
  onTabChange?: (tabId: string) => void;
  /** Slot rendered in the header beside the close button (e.g. PersonalizationToggle). */
  headerAction?: React.ReactNode;
  /** aria-label for the close button. Defaults to "Close". */
  closeLabel?: string;
}

export function Modal({
  title,
  onClose,
  children,
  footer,
  notice,
  width = 'xl',
  tabs,
  activeTab,
  onTabChange,
  headerAction,
  closeLabel,
}: ModalProps) {
  const { t } = useTranslation();
  const resolvedCloseLabel = closeLabel ?? t('common.close', 'Close');
  const { openAbout } = useAbout();
  // Set only by the child drill-down (ChildDetailModal); false everywhere else.
  const childReadOnly = useChildEditorReadOnly();
  // While personalizing, the design panel's Apply is the only commit — the record
  // footer (Save/Cancel) is hidden so it can't be mistaken for "save my layout"
  // (clicking it would save the record, close, and discard the uncommitted layout).
  const personalizing = usePersonalizationOptional()?.isPersonalizing === true;
  const dialogRef   = useRef<HTMLDivElement>(null);
  const onCloseRef  = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; });

  // Ref-stable ESC listener — no re-registration on every parent render.
  // Respects e.defaultPrevented so nested pickers can cancel the event first.
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented) onCloseRef.current();
    };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, []);

  // Move focus into the dialog on mount so keyboard users start inside it.
  useEffect(() => {
    const first = dialogRef.current?.querySelector<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    first?.focus();
  }, []);

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/30" onClick={onClose} />
      {/* P-12: below the sm breakpoint the dialog becomes a full-screen sheet —
          centered floating modals waste half a phone screen and let order-line
          grids collide with the viewport edge. Desktop presentation unchanged. */}
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-0 sm:p-6"
        onClick={onClose}
      >
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
          className={`relative flex w-full ${MAX_WIDTH_CLASS[width]} flex-col h-full max-h-full rounded-none sm:h-auto sm:max-h-[90vh] sm:rounded-csm-card bg-white elev-2 border border-csm-border anim-modal`}
          onClick={(e) => e.stopPropagation()}
        >
          <header className="flex h-10 shrink-0 items-center justify-between border-b border-csm-border px-3">
            <span
              id="modal-title"
              className="text-csm-sm font-semibold uppercase tracking-wider text-csm-ink-soft"
            >
              {title}
            </span>
            <div className="flex items-center gap-2">
              {headerAction}
              <button
                type="button"
                aria-label={t('common.about', 'About')}
                title={t('common.about', 'About')}
                onClick={openAbout}
                className="text-csm-muted hover:text-csm-ink text-base leading-none select-none"
              >
                ℹ
              </button>
              <button
                type="button"
                aria-label={resolvedCloseLabel}
                onClick={onClose}
                className="text-csm-muted hover:text-csm-ink text-xl leading-none"
              >
                ×
              </button>
            </div>
          </header>

          {tabs && tabs.length > 0 && (
            <div className="flex border-b border-csm-border px-3">
              {tabs.map((tab) => (
                <button
                  type="button"
                  key={tab.id}
                  onClick={() => !tab.disabled && onTabChange?.(tab.id)}
                  disabled={tab.disabled}
                  className={`px-3 py-2 text-csm-sm border-b-2 -mb-px transition-colors ${
                    tab.disabled
                      ? 'border-transparent text-csm-ink-soft/40 cursor-not-allowed opacity-50'
                      : activeTab === tab.id
                        ? 'border-primary text-primary font-semibold'
                        : 'border-transparent text-csm-ink-soft hover:text-csm-ink'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          )}

          {notice && (
            <div className="shrink-0 border-b border-csm-border bg-amber-50 px-4 py-2 text-csm-sm">
              {notice}
            </div>
          )}

          {/* Read-only (a child drill-down the caller may not change): the BODY is
              inert, never the chrome. A disabled fieldset around the whole modal
              also disables the × and Cancel, which left the user with no visible
              way out — Escape and the backdrop still worked, but nothing said so. */}
          <div className="min-h-0 flex-1 overflow-y-auto p-4">
            {childReadOnly
              ? <fieldset disabled className="m-0 min-w-0 border-0 p-0" style={{ display: 'contents' }}>{children}</fieldset>
              : children}
          </div>

          {/* …and the record footer is dropped outright, because Modal cannot tell
              which of the caller's buttons is Save. Offering a Save that can only
              be refused is the same defect the RBAC sweep removed elsewhere.
              Nothing replaces it: the header × is the single way out, so a
              read-only viewer is never choosing between two identical exits. */}
          {footer && !childReadOnly && !personalizing && (
            <footer className="flex shrink-0 justify-end gap-2 border-t border-csm-border bg-csm-surface-alt px-3 py-2">
              {footer}
            </footer>
          )}
          {footer && !childReadOnly && personalizing && (
            <footer className="flex shrink-0 items-center justify-center gap-2 border-t border-csm-border bg-primary/5 px-3 py-2 text-csm-xs text-primary">
              {t('common.modal.personalizing_prefix', 'Personalizing layout — click')} <span className="font-semibold">{t('common.apply', 'Apply')}</span> {t('common.modal.personalizing_suffix', 'in the Field Layout Editor to save your changes, then exit personalization. Record editing is paused.')}
            </footer>
          )}
        </div>
      </div>
    </>
  );
}
